/**
 * AutoSelectClient — picks the first available agent after login when no
 * client is currently selected, so per-agent pages (Threat Hunting, FIM, etc.)
 * have an agent id immediately without the user having to open the switcher.
 *
 * Mounts inside AppShell, which only renders for logged-in scoped users; the
 * effect fires once per session because we short-circuit as soon as
 * `selected` is populated (either by this effect, a manual pick, or a value
 * restored from localStorage).
 *
 * HISTORICAL NOTE — why this uses usePickerAgents (not useRegistryAgents):
 * ------------------------------------------------------------------------
 * This component mounts in AppShell, so it runs on EVERY page. Previously it
 * called useRegistryAgents, which hits the heavy GET /agents endpoint (full
 * row shape: IPs, platform string, version, enrolled_at, last_seen, etc.).
 * Combined with ClientSelector's call to the lean GET /agents/picker endpoint
 * from the same TopNav, every page refresh produced TWO network requests to
 * the agents API — one lean, one full — even on pages that have nothing to
 * do with the registry.
 *
 * The picker endpoint already returns everything SelectedClient needs
 * (agent_id + hostname), and it's the exact same TanStack Query cache entry
 * that ClientSelector already populates (same registryKeys.picker(orgId)
 * key). So using usePickerAgents here means:
 *   • AutoSelectClient + ClientSelector share ONE cache entry.
 *   • Only ONE request to /agents/picker per org per 30s staleTime window.
 *   • /agents (full) only fires on the Security Client Registry page itself.
 */
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePickerAgents, useSelectedClient } from "@features/endpoint-inventory/hooks/useRegistry";
import { useEffectiveOrgId } from "./hooks/useOrgPicker";

/* Routes where `?agent=…` doesn't belong — the group-admin launchpads
 * are pre-org pages (no agent concept) and stamping an agent id onto
 * their URL would be visible noise. All other routes get the stamp;
 * that includes org-scoped pages like /endpoint-inventory (harmless
 * extra param, same policy as a user who just arrived from
 * EndpointInventoryPage's own navigate call). */
const NO_AGENT_URL_ROUTES: ReadonlySet<string> = new Set([
  "/organizations",
  "/group-dashboard",
]);

export const AutoSelectClient: React.FC = () => {
  // Resolve the effective org id — JWT org for org admins, picked org
  // for group admins. Without this, group admins never got an auto-
  // picked agent because the picker query was gated on orgId !== "".
  const orgId = useEffectiveOrgId();
  const { selected, select } = useSelectedClient();

  // Picker endpoint is tiny (4 fields per row) and shares its cache entry
  // with ClientSelector in TopNav — one network request serves both.
  const list = usePickerAgents(orgId);

  // Universal URL stamp. Every pick change writes `?agent=…` into the
  // URL (except on the launchpad routes above). This is the codebase's
  // established pattern — see EndpointInventoryPage line 78-79 which does
  // `select() + navigate(?agent=X)` together — and it's the most reliable
  // way to guarantee agent-scoped pages refresh on an org change. Pages
  // that read agent id from the URL re-match on the search-param change;
  // pages that read from useSelectedClient re-render via the hook's own
  // reactivity. Either path (or both) fires on every re-pick.
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  // Belt-and-braces: on every agent change, invalidate ALL react-query
  // caches. Several pages (FIM, SysMon) use
  //     placeholderData: (prev) => prev
  // on their queries, which keeps the PREVIOUS query's data visible
  // while the new fetch loads. If the new fetch doesn't fire reliably
  // (which can happen under concurrent-mode render tearing with the
  // previous useState-based useSelectedClient), the placeholder sticks
  // and the page looks "frozen" on the old agent's data. Invalidate
  // forces every mounted query to re-check its stale status, which
  // triggers a refetch for queries whose key just changed. Nuclear but
  // safe — agent changes are user-driven, not frequent.
  const qc = useQueryClient();

  useEffect(() => {
    // Nothing to decide until we know which org we're in AND the picker
    // response has arrived. The react-query cache keeps data stable across
    // mounts, so after the first fetch this fires immediately on re-render.
    if (!orgId) return;
    if (!list.data) return;

    const canStampUrl = !NO_AGENT_URL_ROUTES.has(pathname);
    const currentAgentParam = searchParams.get("agent");

    /** Helper: set `?agent=value` on the current URL, or delete it when
     *  value is null. Preserves every other query param. Replace-mode
     *  so the browser's Back button doesn't accumulate agent-switch
     *  entries. No-op when the stamp isn't allowed here or when the
     *  URL already matches. */
    const stampUrl = (value: string | null): void => {
      if (!canStampUrl) return;
      if (value === null && !searchParams.has("agent")) return;
      if (value !== null && currentAgentParam === value) return;
      const next = new URLSearchParams(searchParams);
      if (value === null) next.delete("agent");
      else next.set("agent", value);
      setSearchParams(next, { replace: true });
    };

    const first = list.data[0];

    // (A) New org has no agents at all → clear selection and strip any
    //     stale ?agent= from the URL so a reload / bookmark doesn't
    //     resurface the wrong agent.
    if (!first) {
      if (selected) {
        select(null);
        void qc.invalidateQueries();
      }
      stampUrl(null);
      return;
    }

    // (B) Stored selection isn't in the current org's picker list →
    //     either we switched orgs, or the agent was removed. Repick
    //     row 0 and stamp the new id into the URL so the route re-
    //     matches and pages re-fetch with the new agentId.
    const stillValid = selected
      ? list.data.some((a) => a.agent_id === selected.agent_id)
      : false;
    if (!stillValid) {
      select({ agent_id: first.agent_id, hostname: first.hostname });
      void qc.invalidateQueries();
      stampUrl(first.agent_id);
      return;
    }

    // (C) Stored selection IS valid, but the URL's ?agent= might point
    //     to a different agent (deep-link, manual URL edit, stale state).
    //     Push the stored id into the URL so the two sources agree.
    if (selected && currentAgentParam && currentAgentParam !== selected.agent_id) {
      stampUrl(selected.agent_id);
    }
  }, [orgId, list.data, selected, select, searchParams, setSearchParams, pathname, qc]);

  return null;
};
